import { useEffect, useState } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import { NELInput } from '@components/NELInput';
import {
  SegmentCreatorData,
  useSegmentCreatorContext,
} from '@insights_module/context/SegmentCreatorContext';
import { AQLQueryCondition, AQLQueryType } from '@insights_module/adc_ql/types';
import { useFilterTypeOptions } from '@insights_module/hooks/useFilterTypeOptions';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { useFetchLinkedNamedEntities } from '@insights_module/hooks/useFetchLinkedNamedEntities';

export const SegmentCreatorLinkedNamedEntitiesItem = ({
  segmentData,
}: {
  segmentData: SegmentCreatorData;
}) => {
  const { condition, value, operator } = segmentData;
  const { t } = useTranslation();
  const { pushSegmentData, isPresentational } = useSegmentCreatorContext();
  const { isEnabled: isNELEnabled } = useFeatureFlag(FEATURE_FLAG.NAMED_ENTITY_LINKING);

  const { dropdownOptions, allOptions } = useFilterTypeOptions();

  const conditionOptions: IDropdownOption[] = [
    {
      value: 'includes',
      meta: { operator: 'eq' },
      label: <Option>{t('insights.query_builder.conditions.is')}</Option>,
    },
    {
      value: 'excludes',
      meta: { operator: 'eq' },
      label: <Option>{t('insights.query_builder.conditions.is_not')}</Option>,
    },
    {
      value: 'includes',
      meta: { operator: 'wildcard' },
      label: <Option>{t('insights.query_builder.conditions.contains')}</Option>,
    },
    {
      value: 'excludes',
      meta: { operator: 'wildcard' },
      label: <Option>{t('insights.query_builder.conditions.does_not_contain')}</Option>,
    },
  ];

  const [selectedType, setSelectedType] = useState<IDropdownOption>(
    dropdownOptions.find((item) => item.value === segmentData.type) || dropdownOptions[0]
  );
  const [selectedCondition, setSelectedCondition] = useState<IDropdownOption>(
    conditionOptions.find(
      (item) => item.value === (condition as unknown as string) && item.meta?.operator === operator
    ) || conditionOptions[0]
  );
  const [selectedLinkedNamedEntityId, setSelectedLinkedNamedEntityId] = useState<string>(
    (value as string) || ''
  );

  useEffect(() => {
    if (selectedType) {
      const type = selectedType.value as string;
      pushSegmentData({
        ...segmentData,
        type: type as AQLQueryType,
        category: null,
        value: null,
        condition: null,
        negation: false,
      });
    }
  }, [selectedType]);

  useEffect(() => {
    if (selectedCondition && selectedLinkedNamedEntityId) {
      const segment = {
        ...segmentData,
        condition: selectedCondition.value as unknown as Exclude<
          AQLQueryCondition,
          'include' | 'exclude'
        >,
        operator: (selectedCondition.meta?.operator || 'eq') as 'wildcard' | 'eq',
        value: selectedLinkedNamedEntityId,
      };

      pushSegmentData(segment);
    }
  }, [selectedCondition, selectedLinkedNamedEntityId]);

  const { data: entitiyData } = useFetchLinkedNamedEntities(selectedLinkedNamedEntityId);

  useEffect(() => {
    // Edge Case
    // If "linked_named_entities" is found in the query and the user does not have it enabled in FF,
    // we will set a presentation label to avoid a fallback to "Duration" in this case, but we do not add NEL as a dropdown option.
    if (isNELEnabled) return;

    const option = allOptions.find((opt) => opt.value === 'linked_named_entities');
    !!option && setSelectedType(option);
  }, [isNELEnabled]);

  return (
    <div tw='flex gap-1 items-center'>
      <Dropdown
        options={dropdownOptions}
        value={selectedType}
        onChange={(val) => setSelectedType(val as IDropdownOption)}
        labelStyle={tw`bg-spec-entites-filter-bg border border-spec-entites-filter-stroke text-spec-entites-filter-text font-semibold`}
        caretStyle={tw`fill-spec-entites-filter-text`}
        dropdownMenuStyle={tw`w-[178px]`}
        isPresentational={isPresentational || !isNELEnabled}
      />

      <Dropdown
        //for now we don't want to show contains conditions on creating named entities because of performance issues
        options={conditionOptions.slice(0, 2)}
        value={selectedCondition}
        onChange={(val) => setSelectedCondition(val as IDropdownOption)}
        isPresentational={isPresentational || !isNELEnabled}
      />

      <NELInput
        isPresentational={isPresentational || !isNELEnabled}
        onChange={(id) => setSelectedLinkedNamedEntityId(id as string)}
        defaultValue={entitiyData.value}
      />
    </div>
  );
};
